import moment from 'moment';
import type { MergedRawSubscription } from '../../../data/domains/subscription/mergePipeline';
import { appendSubscriptionMenuProbeLog } from './logger';
import type { SubscriptionMenuProbeMergeDecision, SubscriptionMenuProbeMode } from './types';

const PAUSE_EFFECTIVE_DATE_KEYS = [
  'pause_effective_date',
  'paused_effective_date',
  'pause_from_date',
  'pause_start_date',
  'status_effective_date',
  'effective_date',
  'pause_date',
  'paused_from',
  'pause_from',
  'status_change_date',
  'paused_at',
  'pause_at',
  'effective_from',
  'effective_on',
] as const;

function normalize_status(value: string | undefined): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

export function isTerminalSubscriptionStatus(status: string | undefined): boolean {
  const normalized = normalize_status(status);
  return ['expired', 'cancelled', 'canceled', 'completed', 'ended', 'finished'].includes(normalized);
}

export function isActivatedSubscriptionStatus(status: string | undefined): boolean {
  const normalized = normalize_status(status);
  return ['activated', 'active'].includes(normalized);
}

export function isPausedLikeSubscriptionStatus(status: string | undefined): boolean {
  const normalized = normalize_status(status);
  return ['paused', 'pause', 'on_hold'].includes(normalized);
}

function parse_probe_date(value: unknown): string | null {
  if (value == null) {
    return null;
  }
  const text = String(value).trim();
  if (!text) {
    return null;
  }
  const parsed = moment(text, ['YYYY-MM-DD', 'DD MMMM, YYYY', 'DD MMMM YYYY', moment.ISO_8601], true);
  if (!parsed.isValid()) {
    return null;
  }
  return parsed.format('YYYY-MM-DD');
}

function collect_objects_for_scan(raw: MergedRawSubscription): Record<string, unknown>[] {
  const objects: Record<string, unknown>[] = [];
  objects.push(raw as unknown as Record<string, unknown>);
  if (raw.detail != null && typeof raw.detail === 'object') {
    objects.push(raw.detail as Record<string, unknown>);
  }
  if (raw.subscription_detail != null && typeof raw.subscription_detail === 'object') {
    objects.push(raw.subscription_detail as Record<string, unknown>);
  }
  return objects;
}

/** Best-effort parse of pause effective date from merged subscription raw payload. */
export function resolvePauseEffectiveDateFromMerged(raw: MergedRawSubscription): string | null {
  for (const obj of collect_objects_for_scan(raw)) {
    for (const key of PAUSE_EFFECTIVE_DATE_KEYS) {
      const parsed = parse_probe_date(obj[key]);
      if (parsed) {
        return parsed;
      }
    }
  }
  return null;
}

function has_valid_subscription_range(raw: MergedRawSubscription): boolean {
  const start = moment(raw.start_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  const end = moment(raw.end_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  return start.isValid() && end.isValid();
}

function subscription_covers_date(raw: MergedRawSubscription, date_ymd: string): boolean {
  const day = moment(date_ymd, 'YYYY-MM-DD', true);
  const start = moment(raw.start_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  const end = moment(raw.end_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  if (!day.isValid() || !start.isValid() || !end.isValid()) {
    return false;
  }
  return day.isBetween(start, end, 'day', '[]');
}

/** Whether a subscription should contribute menu dates on a given calendar day (state 3). */
export function isMergedSubscriptionMenuEligibleOnDate(
  raw: MergedRawSubscription,
  date_ymd: string,
  pause_effective_date?: string | null,
): boolean {
  if (!subscription_covers_date(raw, date_ymd)) {
    return false;
  }
  if (isTerminalSubscriptionStatus(raw.status)) {
    return false;
  }
  if (isActivatedSubscriptionStatus(raw.status)) {
    return true;
  }
  if (isPausedLikeSubscriptionStatus(raw.status)) {
    const effective = pause_effective_date ?? resolvePauseEffectiveDateFromMerged(raw);
    if (!effective) {
      return false;
    }
    const day = moment(date_ymd, 'YYYY-MM-DD', true);
    const effective_day = moment(effective, 'YYYY-MM-DD', true);
    if (!day.isValid() || !effective_day.isValid()) {
      return false;
    }
    return day.isBefore(effective_day, 'day');
  }
  return false;
}

export type ProbeFilterResult = {
  eligible: MergedRawSubscription[];
  decisions: SubscriptionMenuProbeMergeDecision[];
  pause_effective_by_id: Map<number, string | null>;
};

function log_merge_decisions(mode: SubscriptionMenuProbeMode, decisions: SubscriptionMenuProbeMergeDecision[]): void {
  appendSubscriptionMenuProbeLog({
    mode,
    category: 'subscription_merge',
    label: 'merge filter decisions',
    response: { decisions },
    notes: `${decisions.filter(d => d.included).length}/${decisions.length} subscriptions included for menu`,
  });
}

/** Filter merged subscriptions for menu pipeline based on probe mode. */
export function filterMergedSubscriptionsForProbe(
  merged: MergedRawSubscription[],
  mode: SubscriptionMenuProbeMode,
): ProbeFilterResult {
  const pause_effective_by_id = new Map<number, string | null>();
  const decisions: SubscriptionMenuProbeMergeDecision[] = [];

  if (mode === 'disabled' || mode === 'strict') {
    const eligible = merged.filter(raw => {
      const included = raw.status === 'activated';
      decisions.push({
        subscription_id: raw.id,
        status: raw.status,
        included,
        reason: included ? 'status === activated (strict/default)' : `excluded — status "${raw.status ?? 'unknown'}"`,
      });
      return included;
    });
    if (mode !== 'disabled') {
      log_merge_decisions(mode, decisions);
    }
    return { eligible, decisions, pause_effective_by_id };
  }

  if (mode === 'per_day') {
    const eligible = merged.filter(raw => {
      if (isTerminalSubscriptionStatus(raw.status)) {
        decisions.push({
          subscription_id: raw.id,
          status: raw.status,
          included: false,
          reason: 'terminal lifecycle status',
        });
        return false;
      }
      if (!has_valid_subscription_range(raw)) {
        decisions.push({
          subscription_id: raw.id,
          status: raw.status,
          included: false,
          reason: 'invalid start/end date range',
        });
        return false;
      }
      decisions.push({
        subscription_id: raw.id,
        status: raw.status,
        included: true,
        reason: 'per-day mode — included regardless of status (non-terminal, valid range)',
      });
      return true;
    });
    log_merge_decisions(mode, decisions);
    return { eligible, decisions, pause_effective_by_id };
  }

  // effective_date
  const eligible = merged.filter(raw => {
    const pause_effective = resolvePauseEffectiveDateFromMerged(raw);
    pause_effective_by_id.set(raw.id, pause_effective);

    if (isTerminalSubscriptionStatus(raw.status)) {
      decisions.push({
        subscription_id: raw.id,
        status: raw.status,
        included: false,
        reason: 'terminal lifecycle status',
        pause_effective_date: pause_effective,
      });
      return false;
    }
    if (!has_valid_subscription_range(raw)) {
      decisions.push({
        subscription_id: raw.id,
        status: raw.status,
        included: false,
        reason: 'invalid start/end date range',
        pause_effective_date: pause_effective,
      });
      return false;
    }
    if (isActivatedSubscriptionStatus(raw.status)) {
      decisions.push({
        subscription_id: raw.id,
        status: raw.status,
        included: true,
        reason: 'activated/active status',
        pause_effective_date: pause_effective,
      });
      return true;
    }
    if (isPausedLikeSubscriptionStatus(raw.status)) {
      if (!pause_effective) {
        decisions.push({
          subscription_id: raw.id,
          status: raw.status,
          included: false,
          reason: 'paused-like status but no effective date field found',
          pause_effective_date: null,
        });
        return false;
      }
      const today = moment().startOf('day');
      const effective = moment(pause_effective, 'YYYY-MM-DD', true);
      const included = effective.isValid() && today.isBefore(effective, 'day');
      decisions.push({
        subscription_id: raw.id,
        status: raw.status,
        included,
        reason: included
          ? `paused — today before effective date ${pause_effective}`
          : `paused — today on/after effective date ${pause_effective}`,
        pause_effective_date: pause_effective,
      });
      return included;
    }

    decisions.push({
      subscription_id: raw.id,
      status: raw.status,
      included: false,
      reason: `unsupported status "${raw.status ?? 'unknown'}" for effective-date mode`,
      pause_effective_date: pause_effective,
    });
    return false;
  });

  log_merge_decisions(mode, decisions);
  return { eligible, decisions, pause_effective_by_id };
}

export function countMenuEligibleSubscriptionsForPreview(
  subscriptions: Array<{ status?: string }>,
  mode: SubscriptionMenuProbeMode,
): number {
  if (mode === 'disabled' || mode === 'strict') {
    return subscriptions.filter(s => isActivatedSubscriptionStatus(s.status)).length;
  }
  if (mode === 'per_day' || mode === 'effective_date') {
    return subscriptions.filter(s => !isTerminalSubscriptionStatus(s.status)).length;
  }
  return 0;
}

export function menuFetchAllowsSelection(
  mode: SubscriptionMenuProbeMode,
  menu_builders_count: number,
): { allowed: boolean; note: string } {
  if (mode !== 'per_day') {
    return { allowed: true, note: 'selection gate not applied for this probe mode' };
  }
  const allowed = menu_builders_count > 0;
  return {
    allowed,
    note: allowed
      ? `per-day probe — ${menu_builders_count} menu builder(s) returned`
      : 'per-day probe — menu_builders empty; selection blocked',
  };
}

export { subscription_covers_date, has_valid_subscription_range };
